import { defineComponent as _defineComponent } from 'vue'
import { inject, onMounted, ref } from "vue";
import InlineSvg from "vue-inline-svg";
import Page from "@/components/shared/Page.vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import PaymentHistoryDetail from "@/components/payment/PaymentHistoryDetail.vue";
import PaymentHistoryRow from "@/components/payment/PaymentHistoryRow.vue";
import FilterModal from "@/components/shared/modal/FilterModal.vue";
import BookHistoryDetail from "@/views/book/BookHistoryDetail.vue";
import { IBookInfo } from "@/domain/entities/book";
import { useBookStore } from "@/stores/bookStore";
import { usePaymentStore } from "@/stores/paymentStore";
import {
  IPaymentInfo,
  IPaymentInfoType,
} from "@/domain/entities/payment/paymentInfo.types";
import useCompareMapsAreEqual from "@/use/useCompareMapsAreEqual";
import { computed } from "@vue/reactivity";
import { IPaymentDetailInfo } from "@/domain/entities/payment";
import dayjs from "dayjs";
import {
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  ShowErrorModal,
  ShowToastMessage,
} from "@/types";
import PageModal from "@/components/shared/modal/base/PageModal.vue";


const __default__ = {
  name: "PaymentHistory",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const bookStore = useBookStore();
const paymentStore = usePaymentStore();

const paymentInfos = ref<IPaymentInfo[]>([]);
const filteredPaymentInfos = ref<IPaymentInfo[]>([]);

const FILTER_KEY_PERIOD = "조회기간";
const FILTER_KEY_TYPE = "거래유형";

const PERIOD_VALUE_MONTH_1 = "1개월";
const PERIOD_VALUE_MONTH_6 = "6개월";
const PERIOD_VALUE_MONTH_12 = "12개월";

const TYPE_VALUE_ALL = "전체";
const TYPE_VALUE_PAID = "결제";
const TYPE_VALUE_CANCEL = "취소";

const filters = new Map<string, string[]>();
filters.set(FILTER_KEY_PERIOD, [
  PERIOD_VALUE_MONTH_1,
  PERIOD_VALUE_MONTH_6,
  PERIOD_VALUE_MONTH_12,
]);
filters.set(FILTER_KEY_TYPE, [
  TYPE_VALUE_ALL,
  TYPE_VALUE_PAID,
  TYPE_VALUE_CANCEL,
]);

const currentFilter = ref<Map<string, string>>(new Map());
currentFilter.value.set(FILTER_KEY_PERIOD, "1개월");
currentFilter.value.set(FILTER_KEY_TYPE, "전체");

const currentFilterStr = computed(() =>
  Array.from(currentFilter.value.values()).join(" · ")
);
const searchStartDate = computed(() => {
  let month = 0;
  switch (currentFilter.value.get(FILTER_KEY_PERIOD)) {
    case PERIOD_VALUE_MONTH_1:
      month = 1;
      break;
    case PERIOD_VALUE_MONTH_6:
      month = 6;
      break;
    case PERIOD_VALUE_MONTH_12:
      month = 12;
      break;
  }
  return dayjs().subtract(month, "month").format("YYYY-MM-DD");
});

const shouldShowFilters = ref(false);
const onClickFilterApply = async (filter: Map<string, string>) => {
  shouldShowFilters.value = false;
  if (useCompareMapsAreEqual(currentFilter.value, filters)) return;
  moveScrollToTop();
  currentFilter.value = filter;
  await getPaymentInfos();
  onChangeFilter(filter);
};

const onChangeFilter = (filter: Map<string, string>) => {
  if (currentFilter.value.get(FILTER_KEY_TYPE) === TYPE_VALUE_ALL) {
    filteredPaymentInfos.value = [...paymentInfos.value];
    return;
  }
  filteredPaymentInfos.value = paymentInfos.value.filter(
    (paymentInfo) => paymentInfo?.type === filter.get(FILTER_KEY_TYPE)
  );
};

const content = ref<HTMLElement>();
const moveScrollToTop = () => {
  content.value?.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

const showDetail = ref(false);
const showBookHistoryDetail = ref(false);
const toggleShowBookHistoryDetail = (show: boolean) => {
  showBookHistoryDetail.value = show;
};

const paymentDetailInfo = ref<IPaymentDetailInfo>();
const selectedPaymentInfo = ref<IPaymentInfo>();
const selectedBookInfo = ref<IBookInfo>();

const onClickBookDetailId = async (bookId: string, bookDetailId: string) => {
  const bookInfoDetailResult = await bookStore.getBookInfoDetail(
    bookId,
    bookDetailId
  );
  selectedBookInfo.value = bookInfoDetailResult.data;
  toggleShowBookHistoryDetail(true);
};

const onSelectPaymentInfo = async (payment: IPaymentInfo) => {
  selectedPaymentInfo.value = payment;
  const paymentDetailInfoResult = await paymentStore.getPaymentDetailInfo(
    payment.payUid
  );
  if (!paymentDetailInfoResult.isSuccess) {
    showErrorModal(
      "결제상세 오류",
      paymentDetailInfoResult.errorMessage,
      paymentDetailInfoResult.resultCode,
      paymentDetailInfoResult.errorTime
    );
    return;
  }
  paymentDetailInfo.value = paymentDetailInfoResult.data;
  showDetail.value = true;
};

const onCompleteBookCancel = async () => {
  showDetail.value = true;
  toggleShowBookHistoryDetail(false);
  getPaymentInfos();
  showToastMessage("예약이 취소되었습니다");
};

const getPaymentInfos = async () => {
  const today = dayjs().format("YYYY-MM-DD");
  const paymentType = currentFilter.value.get(FILTER_KEY_TYPE);
  const paymentInfoResult = await paymentStore.getPaymentInfos(
    searchStartDate.value,
    today,
    paymentType === TYPE_VALUE_ALL
      ? undefined
      : (paymentType as IPaymentInfoType)
  );
  if (!paymentInfoResult.isSuccess) {
    showErrorModal(
      "결제목록 오류",
      paymentInfoResult.errorMessage,
      paymentInfoResult.resultCode,
      paymentInfoResult.errorTime
    );
  }
  paymentInfos.value = paymentInfoResult.data ?? [];
  filteredPaymentInfos.value = [...paymentInfos.value];
};

onMounted(() => {
  getPaymentInfos();
});

const __returned__ = { showErrorModal, showToastMessage, bookStore, paymentStore, paymentInfos, filteredPaymentInfos, FILTER_KEY_PERIOD, FILTER_KEY_TYPE, PERIOD_VALUE_MONTH_1, PERIOD_VALUE_MONTH_6, PERIOD_VALUE_MONTH_12, TYPE_VALUE_ALL, TYPE_VALUE_PAID, TYPE_VALUE_CANCEL, filters, currentFilter, currentFilterStr, searchStartDate, shouldShowFilters, onClickFilterApply, onChangeFilter, content, moveScrollToTop, showDetail, showBookHistoryDetail, toggleShowBookHistoryDetail, paymentDetailInfo, selectedPaymentInfo, selectedBookInfo, onClickBookDetailId, onSelectPaymentInfo, onCompleteBookCancel, getPaymentInfos, get InlineSvg() { return InlineSvg }, Page, NavigationBar, PaymentHistoryDetail, PaymentHistoryRow, FilterModal, BookHistoryDetail, PageModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})